<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-4 flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-2 min-w-0">
        <div class="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
          </svg>
        </div>
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Factsheet JSON</h2>
        <span class="text-xs text-gray-400 dark:text-gray-500">Hover any node for its own copy icon</span>
      </div>
      <button type="button" @click="copyAll"
        class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors cursor-pointer flex-shrink-0"
        :class="justCopiedAll
          ? 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
          : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'">
        <svg v-if="justCopiedAll" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
        </svg>
        <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/>
        </svg>
        {{ justCopiedAll ? 'Copied' : 'Copy all' }}
      </button>
    </div>

    <div class="px-5 py-5 overflow-x-auto">
      <VueJsonPretty
        :data="jsonData"
        :deep="2"
        :theme="isDark ? 'dark' : 'light'"
        show-icon
        show-length
        :render-node-actions="true"
        class="text-xs"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import { useOrgComponentVersionsStore } from 'stores/orgComponentVersions.js'

const props = defineProps({
  application: { type: Object, required: true },
})

// The application object only carries current_version (a single reference);
// the full version history lives in its own store, normally only fetched
// once InformationSection's "Show versions" manager is opened. Fetched here
// too so the JSON view's information.versions list is complete even if the
// user never opened that manager this visit.
const versionsStore = useOrgComponentVersionsStore()

// Mirrors LogicCanvas.vue's own dark-mode detection (Mermaid needs an
// explicit theme too) — kept in sync via a MutationObserver since the JSON
// view can stay open while the user flips the app's theme toggle.
const isDark = ref(document.documentElement.classList.contains('dark'))
let observer = null

onMounted(() => {
  observer = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

  versionsStore.fetchIfNeeded(props.application.organization_id, props.application.id)
})

onUnmounted(() => {
  observer?.disconnect()
})

// One JSON object, grouped into the same sections the page itself renders
// (Information, Dependencies, Companies & Departments) so the tree structure
// mirrors the factsheet layout rather than just dumping the flat API response.
const jsonData = computed(() => {
  const app = props.application
  const dependencies = app.dependencies || []

  return {
    information: {
      id: app.id,
      name: app.name,
      description: app.description,
      criticality: app.criticality,
      source: app.source,
      current_version: app.current_version || null,
      versions: versionsStore.entryFor(app.id).records,
      logo_url: app.logo_url,
      default_logo_url: app.default_logo_url,
      added_by_name: app.added_by_name,
      organization_id: app.organization_id,
      created_at: app.created_at,
      updated_at: app.updated_at,
    },
    dependencies,
    companies_departments: {
      departments: app.departments || [],
    },
  }
})

const justCopiedAll = ref(false)
let copiedTimeout = null

async function copyAll() {
  await navigator.clipboard.writeText(JSON.stringify(jsonData.value, null, 2))
  justCopiedAll.value = true
  clearTimeout(copiedTimeout)
  copiedTimeout = setTimeout(() => { justCopiedAll.value = false }, 1500)
}
</script>

<style scoped>
/* vue-json-pretty ships unthemed structural text (keys, brackets, colons,
   indices) with no dark-mode handling of its own — only value colors and
   row hover backgrounds respond to its `theme` prop. Restyled here to match
   the app's design system and stay legible against dark:bg-[#1C2128]. */
:deep(.vjs-tree) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
:deep(.vjs-key) {
  color: #374151;
}
:deep(.vjs-tree-brackets),
:deep(.vjs-colon),
:deep(.vjs-node-index),
:deep(.vjs-comment) {
  color: #9ca3af;
}
:deep(.vjs-value-string) {
  color: #15803d;
}
:deep(.vjs-value-boolean),
:deep(.vjs-value-number) {
  color: #2563eb;
}
:deep(.vjs-value-null),
:deep(.vjs-value-undefined) {
  color: #9ca3af;
  font-style: italic;
}
:deep(.vjs-tree-node:hover) {
  background-color: #f9fafb;
  border-radius: 6px;
}

:global(.dark) :deep(.vjs-key) {
  color: #d1d5db;
}
:global(.dark) :deep(.vjs-tree-brackets),
:global(.dark) :deep(.vjs-colon),
:global(.dark) :deep(.vjs-node-index),
:global(.dark) :deep(.vjs-comment) {
  color: #6b7280;
}
:global(.dark) :deep(.vjs-value-string) {
  color: #4ade80;
}
:global(.dark) :deep(.vjs-value-boolean),
:global(.dark) :deep(.vjs-value-number) {
  color: #60a5fa;
}
:global(.dark) :deep(.vjs-value-null),
:global(.dark) :deep(.vjs-value-undefined) {
  color: #6b7280;
}
:global(.dark) :deep(.vjs-tree-node:hover) {
  background-color: rgba(255, 255, 255, 0.05);
}
</style>
